import SendReview from './SendReview';
import SendForm from './SendForm';
import TransactionCommonModal from '../../common/TransactionCommonModal';
import AddNewAddress from '../../wallet/AddNewAddress';
import { removeCommas, useJunoCommonFunctions } from '../../../../client/Juno/helpers';
import { useSelector } from 'react-redux';
import { apiService } from '../../../../client/common/apiCallService';
import TransactionStatus from '../../TransactionStatus';
import PropTypes from 'prop-types';

const ModalChooser = ({ modals, props, actions }) => {
    switch (modals?.current) {
        case 'send': {
            return <SendForm isModal={true} {...props} />;
        }
        case 'review': {
            return <SendReview {...props} />;
        }
        case 'addNewAddress': {
            return <AddNewAddress {...props} {...modals} {...actions} />;
        }
        case 'status': {
            return <TransactionStatus {...modals} />;
        }
        default: {
            return null;
        }
    }
};

const SendModalChooser = ({
    modals,
    setModals,
    setAmount,
    selectedCrypto = {},
    amount = {},
    setSelectedCrypto,
    cryptoList = [],
    allAddresses = {},
    balancesCrypto,
    onClose,
    getCryptoAddress,
    fetchUsersAddress,
    parentOnBack,
    parentGetCryptoAddress,
}) => {
    const { current = 'send', open } = modals;
    const apiUrl = useSelector(state => state.config.api_url);
    const clientData = useSelector(state => state.juno?.clientData);
    const { getCryptoBalances, getTransactions } = useJunoCommonFunctions();
    let actions = {
        close: () => {
            setModals({ open: false });
            if (onClose) {
                onClose();
            }
        },
    };

    const changeModal = name => {
        setModals(prev => ({ ...prev, current: name }));
    };

    const createCryptoTransaction = async () => {
        const payload = {
            currencyType: 'crypto',
            type: 'Send',
            clientId: clientData?.clientId,
            transactionEmail: localStorage.getItem('user_name'),
            transactionFee:
                selectedCrypto?.selectedBlockainFee &&
                parseFloat(removeCommas(selectedCrypto?.selectedBlockainFee)).toFixed(8),
            transactionDetails: {
                cryptoId: selectedCrypto.selected?.currencyShortName,
                blockchain: selectedCrypto.selectedBlockain,
                cryptoAmount: amount.total,
                baseAmount: amount.value, // field added for transaction verfication
                fromAddress: selectedCrypto.selectedBlockainUsersAddress,
                toAddress: selectedCrypto.selectedToAddress,
            },
        };

        await apiService(
            (window.location.hostname === 'localhost' ? apiUrl : '') +
                '/restapi/create-client-transaction-crypto',
            payload,
            async data => {
                let showFailed = data?.status !== 'pending';

                setModals(prev => ({
                    ...prev,
                    status: showFailed ? 'error' : 'pending',
                    currency: selectedCrypto?.selected?.currencyShortName,
                    amount: amount.total,
                    description: showFailed
                        ? 'Failed'
                        : "Send pending, check your transaction history to track it's progress",
                    onClick: () => {
                        setModals({ open: false });
                        setAmount({ value: '', error: '' });
                        if (onClose) {
                            onClose();
                        }
                    },
                    buttonText: showFailed ? 'CLOSE' : 'Track transaction',
                    current: 'status',
                }));
            },
            err => {
                console.error(err);
                setModals(prev => ({
                    ...prev,
                    status: 'error',
                    description: err,
                    onClick: () => {
                        setModals({ open: false });
                        setAmount({ value: '', error: '' });
                        if (onClose) {
                            onClose();
                        }
                    },
                    buttonText: 'CLOSE',
                    current: 'status',
                }));
            },
        );

        getTransactions();
        await getCryptoBalances();
    };

    const sendTransaction = async () => {
        setModals(prev => ({
            ...prev,
            loading: true,
        }));

        await createCryptoTransaction();

        setModals(prev => ({
            ...prev,
            loading: false,
        }));
    };

    switch (current) {
        case 'review': {
            actions = {
                ...actions,
                onBack: () => changeModal('send'),
                bottomText: 'send now',
                back: true,
                heading: 'review & confirm',
                onBottomClick: () => sendTransaction(),
                loadingText: 'processing',
            };
            break;
        }
        case 'send': {
            actions = {
                ...actions,
                bottomText: 'continue',
                onBottomClick: () => changeModal('review'),
                back: false,
                heading: 'send crypto',
                back: parentOnBack,
                onBack: parentOnBack,
            };
            break;
        }
        case 'addNewAddress': {
            actions = {
                ...actions,
                bottomText: 'add new address',
                onBottomClick: () => changeModal('send'),
                back: true,
                heading: 'add new address',
                onBack: () => changeModal('send'),
            };
            break;
        }
        case 'status': {
            actions = {
                ...actions,
            };
        }
    }

    let { close, onBack, bottomText, onBottomClick, back, heading, loadingText } = actions;

    return ['addNewAddress', 'status'].includes(current) && open ? (
        <ModalChooser
            modals={modals}
            actions={actions}
            props={{
                cryptoList,
                getCryptoAddress: parentGetCryptoAddress || getCryptoAddress,
            }}
        />
    ) : (
        <TransactionCommonModal
            open={open}
            onClose={() => close()}
            onBack={() => onBack()}
            heading={heading}
            bottomButtonText={bottomText}
            onBottomButtonClick={() => onBottomClick()}
            showBackButton={back}
            showCloseIcon={true}
            disabled={
                current == 'send'
                    ? amount.error ||
                      !amount.value ||
                      !selectedCrypto?.selectedToAddress ||
                      selectedCrypto.loading
                    : false
            }
            loading={selectedCrypto.loading || modals.loading}
            loadingText={loadingText}>
            <ModalChooser
                modals={modals}
                setModals={setModals}
                props={{
                    setAmount,
                    selectedCrypto,
                    amount,
                    setSelectedCrypto,
                    cryptoList,
                    allAddresses,
                    modals,
                    setModals,
                    balancesCrypto,
                    fetchUsersAddress,
                }}
            />
        </TransactionCommonModal>
    );
};

SendModalChooser.propTypes = {
    modals: PropTypes.shape({
        current: PropTypes.string,
        open: PropTypes.bool,
        loading: PropTypes.bool,
    }),
    setModals: PropTypes.func,

    setAmount: PropTypes.func,
    selectedCrypto: PropTypes.shape({
        selected: PropTypes.object,
        selectedBlockain: PropTypes.string,
        selectedBlockainUsersAddress: PropTypes.string,
        selectedToAddress: PropTypes.string,
        selectedBlockainFee: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        loading: PropTypes.bool,
    }),
    amount: PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        error: PropTypes.string,
        total: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    setSelectedCrypto: PropTypes.func,
    cryptoList: PropTypes.array,
    allAddresses: PropTypes.shape({
        addresses: PropTypes.array,
        loading: PropTypes.bool,
    }),
};
export default SendModalChooser;
